/**
 * <TutorSettingsPage> — « Paramètres » du masteur (page).
 *
 * Les onglets de l'écran partagé d'aujourd'hui (`SettingsTabNavigator`), devenus des lignes comme
 * chez l'élève (`<StudentSettingsPage>`) : chacune ouvre sa page, qui s'enregistre seule.
 *  - Un tuteur a « Général », « Adresse », « Profil » et « Réglages » ; un candidat, « Général » et
 *    « Réglages » seulement — sans `onOpenAddress` ni `onOpenProfile`, pas de ligne.
 *  - « Profil » ne porte pas de formulaire chez le tuteur : l'onglet n'avait qu'un bouton « Mettre
 *    à jour », qui ouvre « Compléter mon profil » (`<TutorCompleteProfilePage>`). La ligne y va.
 *  - « Fuseau horaire » ouvre l'écran commun à tous les comptes (`<AccountTimezonePage>`).
 *
 * « Adresse », « Réglages », « Mot de passe », « Fuseau horaire » et « Fermer mon compte » sont les
 * pages de l'élève, reprises telles quelles : ce sont les mêmes écrans aujourd'hui.
 */
import React from "react"

import { SettingsSection, SettingsSectionSkeleton, type SettingsSectionRow } from "../../molecules/SettingsSection"
import { AccountSettingsTemplate } from "../../templates/AccountSettingsTemplate"
import { compact } from "../../utils/compact"

export type TutorSettingsPageProps = {
    onBack: () => void
    onOpenGeneral: () => void
    /** Absent pour un candidat. */
    onOpenAddress?: () => void
    /** « Profil » : ouvre « Compléter mon profil ». Absent pour un candidat. */
    onOpenProfile?: () => void
    onOpenPreferences: () => void
    onOpenTimezone?: () => void
    timezoneCity?: string | null
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    testID?: string
}

export function TutorSettingsPage(props: TutorSettingsPageProps) {
    const {
        onBack,
        onOpenGeneral,
        onOpenAddress,
        onOpenProfile,
        onOpenPreferences,
        onOpenTimezone,
        timezoneCity,
        loading = false,
        error,
        onRetry,
        testID,
    } = props

    return (
        <AccountSettingsTemplate
            testID={testID}
            title="Paramètres"
            onBack={onBack}
            loading={loading}
            skeleton={<SettingsSectionSkeleton rows={5} />}
            error={error}
            onRetry={onRetry}
        >
            <SettingsSection
                rows={compact<SettingsSectionRow>([
                    { key: "general", iconName: "user", label: "Général", onPress: onOpenGeneral },
                    onOpenAddress && { key: "address", iconName: "home", label: "Adresse", onPress: onOpenAddress },
                    onOpenProfile && {
                        key: "profile",
                        iconName: "graduation-cap",
                        label: "Profil",
                        description: "Mettre à jour mon profil de tuteur",
                        onPress: onOpenProfile,
                    },
                    { key: "preferences", iconName: "sliders-h", label: "Réglages", onPress: onOpenPreferences },
                    onOpenTimezone && {
                        key: "timezone",
                        iconName: "globe",
                        label: "Fuseau horaire",
                        value: timezoneCity ?? undefined,
                        onPress: onOpenTimezone,
                    },
                ])}
            />
        </AccountSettingsTemplate>
    )
}
